import { useEffect, useState } from 'react';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { useApi } from '../../lib/api';

type SeatBlock = {
  seat_block_id: string;
  section: string;
  row: string;
  seats_json: string;
  seat_count: number;
  section_type: string;
  verified: number;
  verification_photo_url: string | null;
  seat_view_photo_url: string | null;
  notes: string | null;
};

const SECTION_TYPES = [
  'Coaches Club',
  'Mezz Club A',
  'Mezz Club B',
  'Mezz Club B1',
  'Mezz Club C',
  'Field 1 Center',
  'Field 1',
  'Field 2',
  'Field 3 Front',
  'Field 3 Back',
  'Mezzanine Front',
  'Mezzanine Back',
  'Loge',
  'Terrace Center',
  'Terrace Corner',
  'Terrace End Zone',
];

export default function SellerSeatBlocks() {
  const api = useApi();
  const [blocks, setBlocks] = useState<SeatBlock[]>([]);
  const [form, setForm] = useState({
    section: '',
    row: '',
    seats: '',
    section_type: 'Field 2',
  });
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    const { seat_blocks } = await api<{ seat_blocks: SeatBlock[] }>('/sellers/seat-blocks');
    setBlocks(seat_blocks);
  }
  useEffect(() => {
    refresh().catch((e) => setError(e instanceof Error ? e.message : 'error'));
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const seats = form.seats
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!seats.length) {
      setError('Add at least one seat number.');
      return;
    }
    try {
      await api('/sellers/seat-blocks', {
        method: 'POST',
        body: JSON.stringify({
          section: form.section,
          row: form.row,
          seats,
          section_type: form.section_type,
        }),
      });
      setForm({ section: '', row: '', seats: '', section_type: form.section_type });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'error');
    }
  }

  async function upload(seatBlockId: string, kind: 'verification' | 'seat_view', file: File) {
    setUploading(seatBlockId + kind);
    try {
      const form = new FormData();
      form.append('file', file);
      await api(`/sellers/seat-blocks/${seatBlockId}/photo?kind=${kind}`, {
        method: 'POST',
        body: form,
      });
      await refresh();
    } finally {
      setUploading(null);
    }
  }

  return (
    <section>
      <h2>My seat blocks</h2>
      <SignedOut>
        <SignInButton mode="modal" />
      </SignedOut>
      <SignedIn>
        <form className="card" onSubmit={create} style={{ marginBottom: '1.5rem', maxWidth: 560 }}>
          <h3>Submit a new seat block for verification</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Admin will review your verification photo within 48 hours.
          </p>
          <label>
            <span>Section</span>
            <input
              required
              value={form.section}
              onChange={(e) => setForm({ ...form, section: e.target.value })}
              placeholder="e.g. 114"
            />
          </label>
          <label>
            <span>Row</span>
            <input
              required
              value={form.row}
              onChange={(e) => setForm({ ...form, row: e.target.value })}
              placeholder="e.g. 5"
            />
          </label>
          <label>
            <span>Seat numbers (comma or space separated)</span>
            <input
              required
              value={form.seats}
              onChange={(e) => setForm({ ...form, seats: e.target.value })}
              placeholder="e.g. 3, 4"
            />
          </label>
          <label>
            <span>Section type</span>
            <select
              value={form.section_type}
              onChange={(e) => setForm({ ...form, section_type: e.target.value })}
            >
              {SECTION_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
          <button>Submit</button>
          {error && <p style={{ color: 'crimson' }}>{error}</p>}
        </form>

        <div className="grid cols-3">
          {blocks.map((b) => {
            const seats = JSON.parse(b.seats_json) as string[];
            return (
              <div key={b.seat_block_id} className="card">
                <p>
                  <strong>Section {b.section}</strong> · Row {b.row}
                </p>
                <p className="muted">Seats {seats.join(', ')} · {b.section_type}</p>
                <p>
                  <span className="badge">
                    {b.verified ? 'verified' : 'pending verification'}
                  </span>
                </p>
                {!b.verification_photo_url && (
                  <label>
                    <span>Upload verification photo</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) upload(b.seat_block_id, 'verification', f);
                      }}
                      disabled={uploading === b.seat_block_id + 'verification'}
                    />
                  </label>
                )}
                <label>
                  <span>{b.seat_view_photo_url ? 'Replace seat-view photo' : 'Upload seat-view photo (optional)'}</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) upload(b.seat_block_id, 'seat_view', f);
                    }}
                    disabled={uploading === b.seat_block_id + 'seat_view'}
                  />
                </label>
                {b.notes && <p className="muted">Admin note: {b.notes}</p>}
              </div>
            );
          })}
        </div>
      </SignedIn>
    </section>
  );
}
